// Partner-page UK 2026 default options.
//
// The numbers below are educated 2026 estimates against the UK courier-and-storage
// market. They are NOT contractual; the admin can edit or remove any of them.
// Pricing values in particular need validation against current Kit Keeper partner
// agreements — see ops/EMAIL_PARTNER_DEFAULTS_QUESTIONS.txt at the umbrella root.
//
// The ten rate amounts are also written into ops/MOCK_DASHBOARDS_SEED.sql at the
// umbrella root, which seeds a dev database so the dashboards have numbers to
// draw. Nothing binds the two and nothing can: the umbrella is not a git repo and
// is checked out by no pipeline. The copy there is a dev convenience and this file
// is the one to change — a disagreement shows a developer a seeded £6.50 against a
// suggested £6.00 and costs nobody anything. Rate *type keys* are a different
// matter entirely and are no longer duplicated at all; they come from the API, in
// ./rateTypes.
//
// Corrected 2026-09-28: the note this replaces said the values were "mirrored ...
// so dev-DB seeding and the admin form pre-fill stay in sync", which described an
// arrangement that had nothing keeping it true. The seed file also carries no
// service areas and no vehicles, though the backlog entry about it said otherwise.


export interface DefaultServiceArea {
  postcodePrefix: string;
  priority: number;
  region: string;
}

export interface DefaultVehicle {
  name: string;
  vehicleClass: string;
  boxCapacity: number;
  weightCapacityKg: number;
  description: string;
}

// 30 most active UK postcode areas for student-storage demand.
// Priority 100 across the board — admin can re-rank after picking the relevant rows.
export const DEFAULT_SERVICE_AREAS: DefaultServiceArea[] = [
  { postcodePrefix: 'E',  priority: 100, region: 'London' },
  { postcodePrefix: 'EC', priority: 100, region: 'London' },
  { postcodePrefix: 'N',  priority: 100, region: 'London' },
  { postcodePrefix: 'NW', priority: 100, region: 'London' },
  { postcodePrefix: 'SE', priority: 100, region: 'London' },
  { postcodePrefix: 'SW', priority: 100, region: 'London' },
  { postcodePrefix: 'W',  priority: 100, region: 'London' },
  { postcodePrefix: 'WC', priority: 100, region: 'London' },
  { postcodePrefix: 'BR', priority: 100, region: 'Greater London' },
  { postcodePrefix: 'CR', priority: 100, region: 'Greater London' },
  { postcodePrefix: 'DA', priority: 100, region: 'Greater London' },
  { postcodePrefix: 'EN', priority: 100, region: 'Greater London' },
  { postcodePrefix: 'HA', priority: 100, region: 'Greater London' },
  { postcodePrefix: 'IG', priority: 100, region: 'Greater London' },
  { postcodePrefix: 'KT', priority: 100, region: 'Greater London' },
  { postcodePrefix: 'RM', priority: 100, region: 'Greater London' },
  { postcodePrefix: 'SM', priority: 100, region: 'Greater London' },
  { postcodePrefix: 'TW', priority: 100, region: 'Greater London' },
  { postcodePrefix: 'UB', priority: 100, region: 'Greater London' },
  { postcodePrefix: 'WD', priority: 100, region: 'Greater London' },
  { postcodePrefix: 'B',  priority: 100, region: 'Birmingham' },
  { postcodePrefix: 'BS', priority: 100, region: 'Bristol' },
  { postcodePrefix: 'CB', priority: 100, region: 'Cambridge' },
  { postcodePrefix: 'L',  priority: 100, region: 'Liverpool' },
  { postcodePrefix: 'LS', priority: 100, region: 'Leeds' },
  { postcodePrefix: 'M',  priority: 100, region: 'Manchester' },
  { postcodePrefix: 'NG', priority: 100, region: 'Nottingham' },
  { postcodePrefix: 'OX', priority: 100, region: 'Oxford' },
  { postcodePrefix: 'S',  priority: 100, region: 'Sheffield' },
  { postcodePrefix: 'YO', priority: 100, region: 'York' },
];

// Default rate amounts (pence). Used by PricingTab when the admin clicks the
// "+ Add rate type" button — the new row is pre-filled with this amount instead
// of zero. Admin can still edit before saving.
export const DEFAULT_RATE_AMOUNTS_PENCE: Record<string, number> = {
  // Courier
  DOORSTEP_COLLECTION: 600,    // £6.00 — standard doorstep
  TIMESLOT_COLLECTION: 900,    // £9.00 — premium hour-window
  DOORSTEP_RETURN:     600,    // £6.00
  TIMESLOT_RETURN:     900,    // £9.00
  MIN_CHARGE:          1500,   // £15.00 — per-visit minimum
  FAILED_DELIVERY_FEE: 800,    // £8.00  — wasted-trip recovery
  // Warehouse
  STORAGE_PER_BOX_MONTH:  400, // £4.00 / box / month
  INBOUND_PROCESSING:     150, // £1.50 / box receive
  OUTBOUND_PICK:          150, // £1.50 / box pick
  WH_FAILED_DELIVERY_FEE: 500, // £5.00 / failed delivery
};

// DEFAULT_COURIER_RATES / DEFAULT_WAREHOUSE_RATES were removed on 2026-09-28.
// Both were pre-built BillingRateDTO arrays with no importer anywhere in the repo;
// PricingTab pre-fills a new row from DEFAULT_RATE_AMOUNTS_PENCE above and builds
// the row itself. They restated the rate-type keys and their courier/warehouse
// grouping a third time, which is exactly the duplication this file's header now
// disclaims.

// Sensible UK fleet defaults. Capacities are conservative against a 60×40×40 cm
// Kit Keeper box (~96 L); real-world packing is rarely 100% efficient.
export const DEFAULT_VEHICLES: DefaultVehicle[] = [
  { name: 'Van — SWB', vehicleClass: 'SWB_VAN', boxCapacity: 30,  weightCapacityKg: 800,  description: 'Short-wheel-base van (Ford Transit Connect, VW Caddy)' },
  { name: 'Van — MWB', vehicleClass: 'MWB_VAN', boxCapacity: 60,  weightCapacityKg: 1000, description: 'Medium-wheel-base van (Sprinter MWB, Transit MWB)' },
  { name: 'Van — LWB', vehicleClass: 'LWB_VAN', boxCapacity: 90,  weightCapacityKg: 1200, description: 'Long-wheel-base van (Sprinter LWB, Transit Jumbo)' },
  { name: 'Luton',     vehicleClass: 'LUTON',   boxCapacity: 150, weightCapacityKg: 1400, description: 'Luton box van for high-volume university moves' },
];

// DEFAULT_ADDITIONAL_FEES was removed on 2026-09-28: an empty array, exported and
// imported by nobody. Additional fees stay partner-specific by design (fuel
// surcharge, extra-floor levy), so there is nothing to suggest and the tab starts
// that list empty on its own.
